import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import SharedNavigation from "@/components/SharedNavigation";
import { getProfilePhotoUrl } from "@/lib/mediaUrls";
import { BadgeWithModal } from "@/components/BadgeWithModal";

const PERIODS = [
  { key: "all_time", label: "All Time" },
  { key: "monthly", label: "This Month" },
  { key: "weekly", label: "This Week" },
];

const trophyForRank = (rank) => {
  if (rank === 1) return "🥇";
  if (rank === 2) return "🥈";
  if (rank === 3) return "🥉";
  return `#${rank}`;
};

const ReferralLeaderboardPage = () => {
  const [user, setUser] = useState(null);
  const [period, setPeriod] = useState("all_time");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/auth/me");
        setUser(me.data);
      } catch (_e) {
        // anonymous OK
      }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/referral/leaderboard", { params: { period, limit: 50 } });
        if (!cancelled) setRows(r.data.rows || []);
      } catch (_e) {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [period]);

  return (
    <div className="min-h-screen bg-black" data-testid="referral-leaderboard-page">
      <SharedNavigation currentUser={user} />
      <div className="pt-20 px-4 pb-12 max-w-4xl mx-auto">
        <header className="mb-6">
          <h1 className="text-3xl sm:text-4xl font-bold text-white">🎯 Top Recruiters</h1>
          <p className="text-gray-400 mt-1 text-sm">
            The artists growing RGM Studio the most. Share your referral link from your
            {" "}<Link to="/dashboard" className="text-purple-400 underline">dashboard</Link> to climb.
          </p>
        </header>

        <div className="flex flex-wrap gap-2 mb-5">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                period === p.key
                  ? "bg-purple-600/30 border-purple-400 text-white"
                  : "bg-black/40 border-gray-700 text-gray-300 hover:border-purple-500/60"
              }`}
              data-testid={`period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <Card className="p-0 bg-gray-900/40 border-gray-800 overflow-hidden" data-testid="leaderboard-card">
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              No referrals yet for this period. Be the first to share your link!
            </div>
          ) : (
            <div className="divide-y divide-gray-800">
              {rows.map((r) => (
                <Link
                  key={r.user_id}
                  to={`/artist/${r.user_id}`}
                  className="flex items-center gap-3 p-3 hover:bg-purple-900/10 transition-colors"
                  data-testid={`leaderboard-row-${r.rank}`}
                >
                  <div className="w-10 text-center text-lg font-bold text-purple-300">
                    {trophyForRank(r.rank)}
                  </div>
                  <img
                    src={getProfilePhotoUrl(r)}
                    alt={r.artist_name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-medium truncate flex items-center gap-2">
                      {r.artist_name}
                      {r.highest_referral_badge && (
                        <BadgeWithModal type={r.highest_referral_badge} size="small" showLabel={false} />
                      )}
                    </div>
                    {period !== "all_time" && (
                      <div className="text-[11px] text-gray-500">
                        {r.referral_total ?? 0} all-time
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-emerald-300">{r.referral_count}</div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wide">referrals</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <div className="mt-6 text-center">
          {user ? (
            <Link to="/dashboard">
              <Button className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                Get your referral link →
              </Button>
            </Link>
          ) : (
            <Link to="/login">
              <Button className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                Sign up & start referring →
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferralLeaderboardPage;
